import { useEffect, useMemo, useRef } from 'react';
import { Dicom } from '@/models/dicom';
import { GridLayout } from '../ViewerContext';
import {
  ResolvedPaneState,
  SerializedViewerUrlState,
  ViewerUrlState,
  formatViewerUrlState,
  resolvePaneState,
  serializeViewerUrlState,
} from '../utils/viewerUrlState';

export type ViewerUrlStateChangeHandler = (
  next: Pick<SerializedViewerUrlState, 'dicomNumber' | 'search'>,
  options: { replace: boolean }
) => void;

interface UseViewerUrlSyncParams {
  urlState?: ViewerUrlState;
  onUrlStateChange?: ViewerUrlStateChangeHandler;
  sortedDicoms: Dicom[];
  filteredSortedDicoms: Dicom[];
  dicomNumberByUuid: ReadonlyMap<string, number>;
  gridLayout: GridLayout;
  assignedDicoms: (Dicom | null)[];
  pinnedPanes: ReadonlyMap<number, Dicom>;
  selectedViewportIndex: number;
  /** Legacy path-only link. Returns false when the viewer refused it. */
  followDicomNumber: (dicomNumber: number) => boolean;
  /** Show a full URL state (layout, pins and panes). */
  applyPaneState: (layout: GridLayout, resolved: ResolvedPaneState) => void;
}

/**
 * Two-way sync between the viewer's pane state and the reader URL
 * (`/studies/<uuid>/<selectedClip>?layout=RxC&panes=…`).
 */
export function useViewerUrlSync({
  urlState,
  onUrlStateChange,
  sortedDicoms,
  filteredSortedDicoms,
  dicomNumberByUuid,
  gridLayout,
  assignedDicoms,
  pinnedPanes,
  selectedViewportIndex,
  followDicomNumber,
  applyPaneState,
}: UseViewerUrlSyncParams) {
  const current = useMemo(
    () =>
      serializeViewerUrlState({
        gridLayout,
        assignedDicoms,
        pinnedPanes,
        selectedViewportIndex,
        dicomNumberByUuid,
      }),
    [
      gridLayout,
      assignedDicoms,
      pinnedPanes,
      selectedViewportIndex,
      dicomNumberByUuid,
    ]
  );

  // FROM-URL reads state through this ref so it only re-runs on URL changes.
  // Must stay above FROM-URL so it updates first.
  const latest = {
    current,
    gridLayout,
    filteredSortedDicoms,
    onUrlStateChange,
    followDicomNumber,
    applyPaneState,
  };
  const latestRef = useRef(latest);
  useEffect(() => {
    latestRef.current = latest;
  });

  // Last state reported by TO-URL; null forces the next report to replace.
  const lastReportedRef = useRef<SerializedViewerUrlState | null>(null);

  // FROM-URL: apply the URL only when it differs from the screen, which is
  // what breaks the URL <-> state loop.
  useEffect(() => {
    if (!urlState) return;
    const state = latestRef.current;

    if (!urlState.panes) {
      // Legacy /studies/<uuid>/<n> link. Refused -> point the URL back at the
      // screen without adding a history entry.
      if (!state.followDicomNumber(urlState.selectedNumber) && state.current) {
        lastReportedRef.current = state.current;
        const { dicomNumber, search } = state.current;
        state.onUrlStateChange?.({ dicomNumber, search }, { replace: true });
      }
      return;
    }

    const target = formatViewerUrlState(urlState, state.gridLayout);
    if (
      state.current?.dicomNumber === target.dicomNumber &&
      state.current.search === target.search
    ) {
      return;
    }
    const layout = urlState.layout ?? state.gridLayout;
    const resolved = resolvePaneState({
      panes: urlState.panes,
      selectedNumber: urlState.selectedNumber,
      sortedDicoms,
      orderedDicoms: state.filteredSortedDicoms,
      viewportCount: layout.rows * layout.cols,
    });
    // The URL already holds this state — don't push another entry for it.
    lastReportedRef.current = null;
    state.applyPaneState(layout, resolved);
  }, [urlState, sortedDicoms]);

  // TO-URL: only a change of the selected clip pushes a history entry.
  const dicomNumber = current?.dicomNumber;
  const search = current?.search;
  const shape = current?.shape;
  useEffect(() => {
    if (dicomNumber === undefined || search === undefined) return;
    const last = lastReportedRef.current;
    lastReportedRef.current = { dicomNumber, search, shape: shape! };
    const replace =
      !last || last.dicomNumber === dicomNumber || last.shape !== shape;
    onUrlStateChange?.({ dicomNumber, search }, { replace });
  }, [dicomNumber, search, shape, onUrlStateChange]);
}
